import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronRight } from "lucide-react";

export const StepTitle = ({ title, subtitle }) => (
  <div className="text-center mb-6">
    <h2 className="text-xl font-bold text-gray-900" data-testid="calc-question">{title}</h2>
    {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
  </div>
);

export const NextButton = ({ disabled, onClick, label = "Continuer" }) => (
  <Button onClick={onClick} disabled={disabled} className="w-full h-12 mt-6 bg-[#102692] hover:bg-[#0d1f75] text-white rounded-xl" data-testid="calc-next-btn">
    {label} <ChevronRight className="w-4 h-4 ml-1" />
  </Button>
);

export const ChoiceStep = ({ title, subtitle, options, value, onSelect }) => (
  <>
    <StepTitle title={title} subtitle={subtitle} />
    <div className="grid gap-3">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onSelect(o.value)}
          className={`flex items-center justify-between p-4 rounded-2xl border-2 text-left transition-colors ${
            value === o.value ? "border-[#102692] bg-[#102692]/5" : "border-gray-200 hover:border-[#102692]/40"
          }`}
          data-testid={`calc-option-${o.value}`}
        >
          <span className="font-medium text-gray-900">{o.label}</span>
          {value === o.value && <Check className="w-5 h-5 text-[#102692]" />}
        </button>
      ))}
    </div>
  </>
);

export const IconChoiceStep = ({ title, options, value, onSelect }) => (
  <>
    <StepTitle title={title} />
    <div className="grid grid-cols-2 gap-3">
      {options.map(({ value: v, label, icon: Icon }) => (
        <button
          key={v}
          onClick={() => onSelect(v)}
          className={`flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 transition-colors ${
            value === v ? "border-[#102692] bg-[#102692]/5" : "border-gray-200 hover:border-[#102692]/40"
          }`}
          data-testid={`calc-option-${v}`}
        >
          <Icon className="w-12 h-12 text-[#102692]" />
          <span className="font-medium text-gray-900 text-center">{label}</span>
        </button>
      ))}
    </div>
  </>
);

export const MultiChoiceStep = ({ title, subtitle, options, value = [], max, onChange, onNext }) => {
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else if (value.length < max) onChange([...value, v]);
  };
  return (
    <>
      <StepTitle title={title} subtitle={subtitle} />
      <div className="grid gap-3">
        {options.map((o) => (
          <button
            key={o.value}
            onClick={() => toggle(o.value)}
            className={`flex items-center justify-between p-4 rounded-2xl border-2 text-left transition-colors ${
              value.includes(o.value) ? "border-[#102692] bg-[#102692]/5" : "border-gray-200 hover:border-[#102692]/40"
            }`}
            data-testid={`calc-option-${o.value}`}
          >
            <span className="font-medium text-gray-900">{o.label}</span>
            {value.includes(o.value) && <Check className="w-5 h-5 text-[#102692]" />}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-2">{value.length}/{max} sélectionnée(s)</p>
      <NextButton disabled={value.length === 0} onClick={onNext} />
    </>
  );
};

export const InputStep = ({ title, subtitle, value, onNext, type = "number", placeholder, suffix, min = 0, validate }) => {
  const [v, setV] = useState(value ?? "");
  const ok = validate ? validate(v) : String(v).trim() !== "" && (type !== "number" || Number(v) >= min);
  return (
    <>
      <StepTitle title={title} subtitle={subtitle} />
      <div className="relative">
        <Input
          type={type}
          inputMode={type === "number" ? "decimal" : undefined}
          min={type === "number" ? min : undefined}
          value={v}
          placeholder={placeholder}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ok && onNext(type === "number" ? Number(v) : v)}
          className="h-14 text-lg text-center"
          data-testid="calc-input"
          autoFocus
        />
        {suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">{suffix}</span>}
      </div>
      <NextButton disabled={!ok} onClick={() => onNext(type === "number" ? Number(v) : v)} />
    </>
  );
};
